import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import localUtil from '../../lib/util';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;

@observer
class PollEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            question: '',
            options: ['', ''],
            expireDate: '',
            isCreating: false,
            isDeleting: false,
            error: null,
            showEditor: false,
            isEditing: false,
        };
    }

    componentDidMount() {
        this.initFromExistingPoll();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.existingPoll !== this.props.existingPoll) {
            this.initFromExistingPoll();
        }
    }

    initFromExistingPoll = () => {
        const { existingPoll } = this.props;
        if (existingPoll) {
            const expireDate = existingPoll.expireDate
                ? new Date(existingPoll.expireDate).toISOString().slice(0, 16)
                : '';
            this.setState({
                question: existingPoll.question || '',
                options: existingPoll.options || ['', ''],
                expireDate,
            });
        }
    }

    handleQuestionChange = (e) => {
        this.setState({ question: e.target.value });
    }

    handleOptionChange = (idx, value) => {
        const { options } = this.state;
        const newOptions = [...options];
        newOptions[idx] = value;
        this.setState({ options: newOptions });
    }

    handleExpireDateChange = (e) => {
        this.setState({ expireDate: e.target.value });
    }

    addOption = () => {
        const { options } = this.state;
        if (options.length >= MAX_OPTIONS) return;
        this.setState({ options: [...options, ''] });
    }

    removeOption = (idx) => {
        const { options } = this.state;
        if (options.length <= MIN_OPTIONS) return;
        const newOptions = options.filter((_, i) => i !== idx);
        this.setState({ options: newOptions });
    }

    toggleEditor = () => {
        this.setState(prev => ({ showEditor: !prev.showEditor }));
    }

    toggleEditMode = () => {
        this.setState(prev => ({ isEditing: !prev.isEditing }));
    }

    createPoll = async () => {
        const { question, options, expireDate } = this.state;
        const { type, objectId, onPollCreated } = this.props;
        const { pollStore } = this.props.stores;

        if (!question.trim()) {
            this.setState({ error: 'Please enter a question' });
            return;
        }

        const validOptions = options.filter(opt => opt.trim() !== '');
        if (validOptions.length < MIN_OPTIONS) {
            this.setState({ error: `Please enter at least ${MIN_OPTIONS} options` });
            return;
        }

        this.setState({ isCreating: true, error: null });

        try {
            const response = await pollStore.createPoll({
                type,
                objectId,
                question: question.trim(),
                options: validOptions.map(opt => opt.trim()),
                expireDate: expireDate || undefined,
            });

            if (response.status === 201) {
                this.setState({
                    question: '',
                    options: ['', ''],
                    expireDate: '',
                    isCreating: false,
                    showEditor: false,
                });

                if (onPollCreated) {
                    onPollCreated(response.data);
                }
            } else {
                this.setState({
                    isCreating: false,
                    error: response.message || 'Failed to create poll',
                });
            }
        } catch (err) {
            this.setState({
                isCreating: false,
                error: 'Failed to create poll. Please try again.',
            });
        }
    }

    updatePoll = async () => {
        const { question, options, expireDate } = this.state;
        const { existingPoll, onPollCreated } = this.props;
        const { pollStore } = this.props.stores;

        if (!question.trim()) {
            this.setState({ error: 'Please enter a question' });
            return;
        }

        const validOptions = options.filter(opt => opt.trim() !== '');
        if (validOptions.length < MIN_OPTIONS) {
            this.setState({ error: `Please enter at least ${MIN_OPTIONS} options` });
            return;
        }

        this.setState({ isCreating: true, error: null });

        try {
            const response = await util.fetchApi(`/api/polls/${existingPoll.id}`, { method: 'PATCH' }, {
                question: question.trim(),
                options: validOptions.map(opt => opt.trim()),
                expireDate: expireDate || null,
            });

            if (response.status === 200) {
                this.setState({
                    isCreating: false,
                    isEditing: false,
                });

                if (onPollCreated) {
                    onPollCreated(response.data);
                }
            } else {
                this.setState({
                    isCreating: false,
                    error: response.message || 'Failed to update poll',
                });
            }
        } catch (err) {
            this.setState({
                isCreating: false,
                error: 'Failed to update poll. Please try again.',
            });
        }
    }

    deletePoll = async () => {
        const dialogAction = beginDialogAction(this, 'deletePoll', () => [
            this.props?.existingPoll?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { existingPoll, onPollCreated } = this.props;
            const { pollStore } = this.props.stores;

            if (!((await appConfirm('Are you sure you want to delete this poll? All votes will be lost.')) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ isDeleting: true, error: null });

            try {
                const response = await util.fetchApi(`/api/polls/${existingPoll.id}`, { method: 'DELETE' });

                if (response.status === 200) {
                    this.setState({
                        question: '',
                        options: ['', ''],
                        expireDate: '',
                        isDeleting: false,
                        isEditing: false,
                    });

                    if (onPollCreated) {
                        onPollCreated(null);
                    }
                } else {
                    this.setState({
                        isDeleting: false,
                        error: response.message || 'Failed to delete poll',
                    });
                }
            } catch (err) {
                this.setState({
                    isDeleting: false,
                    error: 'Failed to delete poll. Please try again.',
                });
            }
        } finally {
            dialogAction.finish();
        }
    }

    renderExistingPoll = () => {
        const { existingPoll } = this.props;
        const { isEditing, isDeleting, error } = this.state;
        const votes = existingPoll.votes || [];
        const totalVotes = votes.length;

        if (isEditing) {
            return this.renderEditForm();
        }

        // Calculate vote counts per option
        const voteCounts = existingPoll.options.map((_, idx) =>
            votes.filter(v => v.optionIndex === idx).length
        );

        return (
            <div class='card mb-3'>
                <div class='card-header d-flex justify-content-between align-items-center'>
                    <span>
                        <i class='fa-solid fa-poll me-2' />
                        <Text id='poll.existing-poll'>Existing Poll</Text>
                    </span>
                    <div>
                        <button
                            type='button'
                            class='btn btn-outline-primary btn-sm me-2'
                            onClick={this.toggleEditMode}
                        >
                            <i class='fa-solid fa-pencil me-1' />
                            <Text id='common.edit'>Edit</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-outline-danger btn-sm'
                            onClick={this.deletePoll}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <span class='spinner-border spinner-border-sm' />
                            ) : (
                                <i class='fa-solid fa-trash' />
                            )}
                        </button>
                    </div>
                </div>
                <div class='card-body'>
                    {error && (
                        <div class='alert alert-danger mb-3'>
                            {error}
                        </div>
                    )}

                    {/* Question */}
                    <h6 class='mb-3'>{existingPoll.question}</h6>

                    {/* Options with vote counts */}
                    <div class='mb-3'>
                        {existingPoll.options.map((option, idx) => {
                            const voteCount = voteCounts[idx];
                            const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                            return (
                                <div key={idx} class='d-flex align-items-center mb-2'>
                                    <div
                                        class='d-flex justify-content-center align-items-center fw-bold me-2 rounded-3 bg-light border'
                                        style='width: 28px; height: 28px; min-width: 28px; font-size: 0.85em;'
                                    >
                                        {OPTION_LABELS[idx]}
                                    </div>
                                    <div class='flex-fill'>
                                        <div class='d-flex justify-content-between'>
                                            <span>{option}</span>
                                            <small class='text-muted'>{voteCount} ({percentage}%)</small>
                                        </div>
                                        <div class='progress' style='height: 4px;'>
                                            <div
                                                class='progress-bar'
                                                style={`width: ${percentage}%;`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Stats */}
                    <div class='d-flex justify-content-between text-muted small'>
                        <span>
                            <i class='fa-solid fa-users me-1' />
                            {totalVotes} <Text id='poll.total-votes'>votes</Text>
                        </span>
                        {existingPoll.expireDate && (
                            <span>
                                <i class='fa-solid fa-clock me-1' />
                                <Text id='poll.expires'>Expires</Text>: {localUtil.formatDate(existingPoll.expireDate)}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderEditForm = () => {
        const { question, options, expireDate, isCreating, error } = this.state;
        const { existingPoll } = this.props;
        const isUpdate = !!existingPoll;

        return (
            <div class='card mb-3'>
                <div class='card-header d-flex justify-content-between align-items-center'>
                    <span>
                        <i class='fa-solid fa-poll me-2' />
                        {isUpdate ? (
                            <Text id='poll.edit-poll'>Edit Poll</Text>
                        ) : (
                            <Text id='poll.create-poll'>Create Poll</Text>
                        )}
                    </span>
                    <button
                        type='button'
                        class='btn-close'
                        onClick={isUpdate ? this.toggleEditMode : this.toggleEditor}
                    />
                </div>
                <div class='card-body'>
                    {error && (
                        <div class='alert alert-danger mb-3'>
                            {error}
                        </div>
                    )}

                    {/* Question */}
                    <div class='mb-3'>
                        <label class='form-label fw-bold'>
                            <Text id='poll.question'>Question</Text>
                        </label>
                        <Localizer>
                            <input
                                type='text'
                                class='form-control'
                                value={question}
                                onInput={this.handleQuestionChange}
                                placeholder={<Text id='poll.question-placeholder'>Ask a question...</Text>}
                            />
                        </Localizer>
                    </div>

                    {/* Options */}
                    <div class='mb-3'>
                        <label class='form-label fw-bold'>
                            <Text id='poll.options'>Options</Text>
                        </label>
                        {options.map((option, idx) => (
                            <div key={idx} class='d-flex flex-row align-items-center mb-2'>
                                <div
                                    class='d-flex justify-content-center align-items-center fw-bold me-2 rounded-3 bg-light border'
                                    style='width: 32px; height: 38px; min-width: 32px;'
                                >
                                    {OPTION_LABELS[idx]}
                                </div>
                                <input
                                    type='text'
                                    class='form-control'
                                    value={option}
                                    onInput={(e) => this.handleOptionChange(idx, e.target.value)}
                                    placeholder={`Alternativ ${OPTION_LABELS[idx]}`}
                                />
                                {options.length > MIN_OPTIONS && (
                                    <button
                                        type='button'
                                        class='btn btn-outline-danger btn-sm ms-2'
                                        onClick={() => this.removeOption(idx)}
                                    >
                                        <i class='fa-solid fa-trash' />
                                    </button>
                                )}
                            </div>
                        ))}
                        {options.length < MAX_OPTIONS && (
                            <button
                                type='button'
                                class='btn btn-outline-secondary btn-sm mt-2'
                                onClick={this.addOption}
                            >
                                <i class='fa-solid fa-plus me-1' />
                                <Text id='poll.add-option'>Add option</Text>
                            </button>
                        )}
                        <small class='text-muted d-block mt-1'>
                            {options.length} / {MAX_OPTIONS} <Text id='poll.options-label'>options</Text>
                        </small>
                    </div>

                    {/* Expiration Date */}
                    <div class='mb-3'>
                        <label class='form-label fw-bold'>
                            <Text id='poll.expire-date'>Expiration Date (optional)</Text>
                        </label>
                        <input
                            type='datetime-local'
                            class='form-control'
                            value={expireDate}
                            onInput={this.handleExpireDateChange}
                        />
                        <small class='text-muted'>
                            <Text id='poll.expire-date-help'>Leave empty for no expiration</Text>
                        </small>
                    </div>

                    {/* Buttons */}
                    <div class='d-flex justify-content-end'>
                        <button
                            type='button'
                            class='btn btn-secondary me-2'
                            onClick={isUpdate ? this.toggleEditMode : this.toggleEditor}
                        >
                            <Text id='common.cancel'>Cancel</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-primary'
                            onClick={isUpdate ? this.updatePoll : this.createPoll}
                            disabled={isCreating}
                        >
                            {isCreating ? (
                                <>
                                    <span class='spinner-border spinner-border-sm me-2' />
                                    <Text id='common.saving'>Saving...</Text>
                                </>
                            ) : (
                                <>
                                    <i class='fa-solid fa-check me-2' />
                                    {isUpdate ? (
                                        <Text id='common.save'>Save</Text>
                                    ) : (
                                        <Text id='poll.create'>Create Poll</Text>
                                    )}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { showEditor } = this.state;
        const { existingPoll } = this.props;

        // If there's an existing poll, show it with edit/delete options
        if (existingPoll) {
            return this.renderExistingPoll();
        }

        // No existing poll - show create button or editor
        if (!showEditor) {
            return (
                <div class='mb-3'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary btn-sm'
                        onClick={this.toggleEditor}
                    >
                        <i class='fa-solid fa-poll me-2' />
                        <Text id='poll.add-poll'>Add Poll</Text>
                    </button>
                </div>
            );
        }

        return this.renderEditForm();
    }
}

export default PollEditor;
